import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import { PathParams, PickupLogPath } from "./route";
import { Routes } from "../../../app/route/RoutesConfig";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSFormInput from "../../../ui-components/form/input/xgsFormInput";
import LabeledDateRangeInput from "../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import XGSFormSelect from "../../../ui-components/form/select/xgsFormSelect";
import Table from "../../../ui-components/table/table";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import ContentContainer from "../../../templates/content-container/contentContainer";
import { userSelector } from "../../../slices/user/userSlice";
import {
  clearPickupsLog,
  getPickupsLog,
  pickupAssignmentSelector
} from "../../../slices/pickup/pickupAssignmentSlice";
import {
  PickupsLogFilterModel,
  PickupsLogFilterSchema
} from "../../../app/data/pickup/models";
import { HUMAN_FRIENDLY_ACTIONS } from "../common";
import PickupDetails from "../../common/pickup-details/pickupDetails";
import { getPickup, pickupDetailsSelector } from "../../../slices/pickup/pickupDetailsSlice";
import { getPickupDetails } from "../../../services/pickups";
import { PAGE_IDS } from "../../../app/data/common/pageIds";

import "./index.scss";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import { formatAdditionalInfo, extractRemainingTextAfterReason } from "../utils/pickupLogUtils";

const initialValues = {
  actionPerformed: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  userEmail: "",
  trackingNumber: ""
};

const columnHelper = createColumnHelper<any>();

const PickupLog: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const params = useParams() as PathParams;
  const userState = useSelector(userSelector);
  const pickupAssignmentState = useSelector(pickupAssignmentSelector);
  const pickupDetailsState = useSelector(pickupDetailsSelector);
  const filterFormRef = useRef<FormikProps<PickupsLogFilterModel>>(null);
  const [filter, setFilter] = useState<PickupsLogFilterModel>(initialValues);
  const [pickupDetailsNumber, setPickupDetailsNumber] = useState<string>("");
  const pickupNumber = useMemo(() => params[PickupLogPath.pickupNumber], [params]);
  const pickupDetails = pickupDetailsState.pickup && getPickupDetails(pickupDetailsState.pickup);

  const location = useLocation<{ fromBolSearch?: boolean }>();
  const searchParams = new URLSearchParams(location.search);
  const fromBolSearch = searchParams.get("fromBolSearch") === "true";
  const [autoOpened, setAutoOpened] = useState(false);

  const pickupList = pickupAssignmentState.pickupsLog;

  const columns = [
    columnHelper.accessor("actionExecutedTime", {
      id: "actionExecutedTime",
      header: "Event Time",
      cell: (cellProps) => (
        <>
          {cellProps.getValue() && (
            <div className="xgs-pickup-details__log__date">
              <div>{cellProps.getValue().toUiDateFormat()}</div> <div className="xgs-pickup-details__log__gray-value">{cellProps.getValue().toUiTimeShortFormat()}</div>
            </div>
          )}
        </>
      ),
      size: 148,
    }),
    columnHelper.accessor("pickupNumber", {
      id: "pickupNumber",
      header: "Pickup #",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowPickupDetails(cellProps.getValue());
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 100,
    }),
    columnHelper.accessor("userExecuted", {
      id: "userExecuted",
      header: "User",
      size: 200,
    }),
    columnHelper.accessor("actionPerformed", {
      id: "actionPerformed",
      header: "Action",
      cell: (cellProps: any) => {
        const value = cellProps.getValue();
        return HUMAN_FRIENDLY_ACTIONS[value as keyof typeof HUMAN_FRIENDLY_ACTIONS] || value;
      },
      size: 160,
    }),
    columnHelper.display({
      id: "additionalInfo",
      header: "Additional Info",
      size: 340,
      cell: ({ row }) => {
        const data = row.original;

        const isCompletedByTerminal =
          data?.actionSource === "PORTAL" && data?.actionPerformed === "COMPLETED";

        const isCompletedByDriver =
          data?.actionSource === "DRIVER" && data?.actionPerformed === "COMPLETED";

        const driverInfo = data.driverName ? (
          <span className="xgs-pickup-details__log__additional__item">
            {data.driverName}{" "}
            {data.driverId && (
              <span className="xgs-pickup-details__log__gray-value">
                ({data.driverId})
              </span>
            )}
          </span>
        ) : data.driverId ? (
          <span className="xgs-pickup-details__log__additional__item">
            {data.driverId}
          </span>
        ) : null;

        if (data?.actionPerformed === "ASSIGNED") {
          return (<>
            {driverInfo}
          </>);
        }

        const renderAdditionalInfo = () => {
          if (isCompletedByTerminal) {
            return (
              <span className="xgs-pickup-details__log__additional__item">
                <Tag mods={{ color: TagColor.RED }}>Completed at Terminal</Tag>
              </span>
            );
          }

          const { reason, formattedText } = formatAdditionalInfo(data);

          if (!reason && !formattedText) return null;

          if (!reason) {
            return (
              <>
                <span
                  className="xgs-pickup-details__log__additional__item"
                  style={{ fontWeight: "normal" }}
                >
                  {formattedText}
                </span>
              </>
            );
          }

          const remainingText = extractRemainingTextAfterReason(formattedText, reason);

          return (
            <>
              <span className="xgs-pickup-details__log__additional__item">
                <strong>{reason}</strong>
              </span>
              {remainingText && (
                <>
                  {", "}
                  <span
                    className="xgs-pickup-details__log__additional__item"
                    style={{ fontWeight: "normal" }}
                  >
                    {remainingText}
                  </span>
                </>
              )}
            </>
          );
        };

        return (
          <div
            className="xgs-pickup-log__additional-info"
            title={data.notes || ""}
          >
            {isCompletedByDriver && (
              <>{driverInfo}</>
            )}
            {renderAdditionalInfo()}
          </div>
        );
      },
    })
  ];

  const getActionPerformedValues = () => {
    return Object.keys(HUMAN_FRIENDLY_ACTIONS).map((key: string) => {
      return {
        value: key,
        label: HUMAN_FRIENDLY_ACTIONS[key as keyof typeof HUMAN_FRIENDLY_ACTIONS]
      };
    });
  };

  const onListInfiniteScroll = () => {
    if (pickupAssignmentState.pickupsLogRequest.last) return;
    const nextPage = pickupAssignmentState.pickupsLogRequest.page + 1;
    dispatch(getPickupsLog(filter, nextPage));
  };

  const onFilterSubmit = (data: PickupsLogFilterModel) => {
    if (data.dateFrom) data.dateFrom = data.dateFrom.toApiDateFormat();
    if (data.dateTo) data.dateTo = data.dateTo.toApiDateFormat();
    if (data.trackingNumber !== pickupNumber)
      window.history.replaceState(window.history.state, "", `${userState.activeTerminal?.id ? "/" + userState.activeTerminal?.id : ""}${Routes.pickup.log}`);
    setFilter(data);
    setPickupDetailsNumber("");
    dispatch(getPickupsLog(data));
  };

  const onShowPickupDetails = useCallback((pickupNumber: string) => {
    if (pickupDetailsNumber !== pickupNumber) {
      setPickupDetailsNumber(pickupNumber);
      dispatch(getPickup(pickupNumber));
    }
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPickupsLog(pickupNumber ? { trackingNumber: pickupNumber } : {}));
    if (pickupNumber && !fromBolSearch) {
      setFilter({ trackingNumber: pickupNumber });
      filterFormRef.current?.setFieldValue("trackingNumber", pickupNumber);
      onShowPickupDetails(pickupNumber);
    }
    return () => {
      dispatch(clearPickupsLog());
    }
  }, [dispatch, pickupNumber, fromBolSearch, onShowPickupDetails]);

  useEffect(() => {
    if (fromBolSearch && !autoOpened && pickupList?.length > 0) {
      onShowPickupDetails(pickupList[0].pickupNumber);
      setAutoOpened(true);
    }
  }, [fromBolSearch, autoOpened, pickupList, onShowPickupDetails]);

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      pageId={PAGE_IDS.PICKUP_LOG}
      title="Pickup Log"
      mods={{ "full-width": true }}
    >
      <div className="xgs-pickup-log">
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search">
            <Formik
              initialValues={initialValues}
              onSubmit={onFilterSubmit}
              validationSchema={PickupsLogFilterSchema}
              innerRef={filterFormRef}
            >
              {(props: FormikProps<PickupsLogFilterModel>) => (
                <Form className="xgs-list__controls__search-form">
                  <XGSFormSelect
                    name="actionPerformed"
                    label="Performed Action"
                    labelMode={LabelModes.column}
                    placeholder="Select..."
                    options={getActionPerformedValues()}
                    isMulti
                    isClearable
                    onValueChange={(o) => {
                      props.setFieldValue("actionPerformed", o?.value || undefined);
                      if (
                        (pickupAssignmentState.requestStarted && (pickupAssignmentState.requestCreator === "GET_PICKUPS_LOG"))
                        || !props.isValid
                      ) return;
                      onFilterSubmit({ ...props.values, actionPerformed: o?.value.toString() })
                    }}
                    controlled={true}
                    className="xgs-item-form__field"
                    onMultiValuesChange={() => {props.submitForm()}}                      
                  />
                  <LabeledDateRangeInput
                    label="Date Range:"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    start={props.values.dateFrom}
                    end={props.values.dateTo}
                    maxDate={new Date(moment().format("MM/DD/YYYY"))}
                    onStartChange={(v) => {
                      props.setFieldValue("dateFrom", v || undefined);
                      props.submitForm();
                    }}
                    onEndChange={(v) => {
                      props.setFieldValue("dateTo", v || undefined);
                      props.submitForm();
                    }}
                  />
                  <XGSFormInput
                    label="User:"
                    name="userEmail"
                    labelMode={LabelModes.column}
                    onChange={(e) => {
                      props.setFieldValue("userEmail", e.currentTarget.value);
                      props.handleChange(e);
                    }}
                    onKeyDown={(e) => {
                      props.setFieldValue("userEmail", e.currentTarget.value || undefined);
                      if (pickupAssignmentState.requestStarted && (pickupAssignmentState.requestCreator === "GET_PICKUPS_LOG")) return;
                      if (e.key === "Enter" && props.isValid) onFilterSubmit({ ...props.values, userEmail: e.currentTarget.value });
                      props.handleChange(e);
                    }}
                    type="text"
                    placeholder="someone@xgsi.com"
                    className="xgs-item-form__field"
                  />
                  <XGSFormInput
                    label="Tracking Number:"
                    labelMode={LabelModes.column}
                    name="trackingNumber"
                    onChange={(e) => {
                      props.setFieldValue("trackingNumber", e.currentTarget.value);
                      props.handleChange(e);
                    }}
                    onKeyDown={(e) => {
                      props.setFieldValue("trackingNumber", e.currentTarget.value || undefined);
                      if (pickupAssignmentState.requestStarted && (pickupAssignmentState.requestCreator === "GET_PICKUPS_LOG")) return;
                      if (e.key === "Enter" && props.isValid) onFilterSubmit({ ...props.values, trackingNumber: e.currentTarget.value });
                      props.handleChange(e);
                    }}
                    type="text"
                    placeholder="Pickup #, BOL, or PO"
                    className="xgs-item-form__field"
                  />
                  <Button
                    type="submit"
                    theme={ButtonThemes.blue}
                    disabled={
                      (pickupAssignmentState.requestStarted && (pickupAssignmentState.requestCreator === "GET_PICKUPS_LOG"))
                    }
                    className="xgs-list__controls__button"
                  >
                    Search
                  </Button>

                </Form>
              )}
            </Formik>
          </div>
        </div>
        {!pickupAssignmentState.requestFailed && pickupAssignmentState.pickupsLog && (
          <Table
            isLoading={pickupAssignmentState.requestStarted && (pickupAssignmentState.requestCreator === "GET_PICKUPS_LOG")}
            columns={columns}
            data={pickupAssignmentState.pickupsLog}
            rowHeight={48}
            minTableHeight={220}
            noResultsText="No entries"
            infiniteScroll
            infiniteScrollLoading={pickupAssignmentState.fetchPortionStarted && pickupAssignmentState.requestCreator === "GET_PICKUPS_LOG_PORTION"}
            infiniteScrollHasNext={!pickupAssignmentState.pickupsLogRequest.last}
            onInfiniteScroll={onListInfiniteScroll}
            responsive
          />
        )}
        {pickupAssignmentState.requestFailed && (
          <XGSErrorMessage className="xgs-pickup-log__error">{pickupAssignmentState.requestError || "Error while obtaining the log"}</XGSErrorMessage>
        )}
      </div>

      <PickupDetails
        isOpen={!!pickupDetailsNumber}
        pickup={pickupDetails}
        onClose={() => setPickupDetailsNumber("")}
        isLoading={pickupDetailsState.requestStarted && pickupDetailsState.requestCreator === "GET_PICKUP"}
        readonly
        error={pickupDetailsState.requestCreator === "GET_PICKUP" ? pickupDetailsState.requestError : ""}
      />
    </ContentContainer>
  );
};

export default PickupLog;
